import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { Link, Container, Typography, Grid } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";
import Logo from "../../../components/Logo";
import LanguagePopover from "../../../layouts/dashboard/header/LanguagePopover";
import { useVerifyClientMutation } from "../loginSlice";
import Loader from "../../../components/Loader";
import { toErrorMsg } from "../../../utils";

const StyledRoot = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const StyledSection = styled("div")(({ theme }: any) => ({
  width: "100%",
  maxWidth: 480,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  padding: theme.spacing(12, 0),
}));

export const VerifyClient = () => {
  const { t } = useTranslation();
  const mdUp = useResponsive("up", "md");
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;
  const [isClientVerified, setIsClientVerified] = useState(false);
  const [message, setMessage] = useState("");
  const calledRef = useRef<boolean>(false);

  const [verify, { isLoading }] = useVerifyClientMutation();

  useEffect(() => {
    if (!calledRef.current) {
      calledRef.current = true;
      verifyClient();
    }
  }, []);

  const verifyClient = async () => {
    try {
      const { isVerified, error, invalidData }: any = await verify({
        id,
      }).unwrap();
      if (isVerified) {
        setIsClientVerified(true);
      }
      if (error) {
        setMessage(toErrorMsg(error));
      }
      if (invalidData) {
        setMessage(toErrorMsg(invalidData));
      }
    } catch (error: any) {
      setMessage(error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${t("VerifyOrganization")} | ${t("AppTitle")}`}</title>
      </Helmet>

      <StyledRoot>
        <Logo
          sx={{
            position: "fixed",
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />
        {mdUp && (
          <StyledSection>
            <Grid sx={{ top: "10%", position: "fixed", maxWidth: "480px" }}>
              <Typography variant="h3" sx={{ px: 5, mt: 10 }}>
                {t("Get Verified")}
              </Typography>
              <img
                style={{
                  marginLeft: "8%",
                  maxWidth: "80%",
                }}
                src="/assets/illustrations/verified.png"
                alt=""
              />
            </Grid>
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            {/* {!isClientVerified && ( */}
            <Fragment>
              <Typography variant="h5" gutterBottom>
                {t("VerifyOrganization")}
              </Typography>

              <Typography variant="body2" sx={{ mb: 5 }}>
                <Link
                  variant="subtitle2"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                >
                  {t("GotoLogin")}
                </Link>
              </Typography>
            </Fragment>
            {/* )} */}

            {isLoading && <Loader />}
            {isClientVerified && (
              <Fragment>
                <Typography variant="h4" sx={{ color: "#02540b" }}>
                  {t("YourOrganizationIsVerified")}
                </Typography>

                <Typography variant="h4">
                  {t("StoreYourSecurityCode")}
                </Typography>

                <Typography variant="h4">
                  <Link
                    variant="h4"
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate("/login")}
                  >
                    {t("PleaseLoginNow")}
                  </Link>
                </Typography>
              </Fragment>
            )}
            {message?.length > 0 && (
              <Typography variant="h4" sx={{ color: "#a3262c" }}>
                {message}
              </Typography>
            )}
          </StyledContent>
        </Container>
      </StyledRoot>

      <span style={{ position: "fixed", top: "10px", right: "10px" }}>
        <LanguagePopover />
      </span>
    </>
  );
};
